import { supabase } from '../lib/supabase';

/**
 * A simple service to initialize database tables for the application
 */
class DbInitService {
  /**
   * Initialize the database with required tables
   */
  async initialize() {
    console.log('Initializing database...');
    
    try {
      // Create feedback_requests table if needed
      await this.ensureFeedbackRequestsTable();
      
      // Always ensure localStorage backup exists
      this.ensureLocalStorageBackups();
      
      return { success: true };
    } catch (error) {
      console.error('Database initialization failed:', error);
      
      // Even if Supabase fails, ensure localStorage is set up
      this.ensureLocalStorageBackups();
      
      return { success: false, error };
    }
  }
  
  /**
   * Create the feedback_requests table if it doesn't exist
   */
  async ensureFeedbackRequestsTable() {
    try {
      // Try to query the table to see if it exists
      const { error } = await supabase
        .from('feedback_requests')
        .select('count(*)')
        .eq('id', 0); // Just a dummy query to check table existence
      
      if (error && error.code === '42P01') {
        // Table doesn't exist, create it
        console.log('Creating feedback_requests table...');
        
        // We'll use a simpler approach with SQL rpc
        const { error: createError } = await supabase.rpc('exec_sql', {
          sql: `
            CREATE TABLE feedback_requests (
              id SERIAL PRIMARY KEY,
              patient_id TEXT NOT NULL,
              appointment_id TEXT NOT NULL,
              email_sent BOOLEAN DEFAULT false,
              sent_at TIMESTAMP WITH TIME ZONE NOT NULL,
              created_at TIMESTAMP WITH TIME ZONE DEFAULT NOW(),
              response_received BOOLEAN DEFAULT false,
              response_received_at TIMESTAMP WITH TIME ZONE
            );
            
            CREATE INDEX idx_feedback_requests_patient_id 
              ON feedback_requests(patient_id);
            CREATE INDEX idx_feedback_requests_appointment_id 
              ON feedback_requests(appointment_id);
          `
        });
        
        if (createError) {
          console.error('Error creating table:', createError);
          throw createError;
        }
        
        console.log('feedback_requests table created successfully');
      } else {
        console.log('feedback_requests table already exists');
      }
    } catch (error) {
      // If this fails, we'll use localStorage as fallback
      console.error('Error ensuring feedback_requests table:', error);
      throw error;
    }
  }
  
  /**
   * Ensure localStorage has necessary structures for fallback
   */
  ensureLocalStorageBackups() {
    // Initialize feedback_requests storage if not present
    if (!localStorage.getItem('feedback_requests')) {
      localStorage.setItem('feedback_requests', JSON.stringify([]));
    }
    
    // Fallback for appointment data (from memory)
    if (!localStorage.getItem('appointments')) {
      localStorage.setItem('appointments', JSON.stringify([]));
    }
    
    // Fallback for patient data (from memory)
    if (!localStorage.getItem('patients')) {
      localStorage.setItem('patients', JSON.stringify([]));
    }
  }
}

export default new DbInitService();
